import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useConceptEditor } from '@/hooks/UseConceptEditor'
import { useConceptAIParser } from '@/hooks/UseConceptAIParser'
import { useCanvasDrawing } from '@/hooks/UseCanvasDrawing'
import { useKeyboardShortcuts } from '@/hooks'
import { EditorPageLayout } from '@/components/layout/EditorPageLayout'
import { ConceptCanvas } from '@/components/canvas/ConceptCanvas'
import { ConceptToolbar } from '@/components/concepts/ConceptToolbar'
import { ConceptHeader } from '@/components/concepts/ConceptHeader'
import { ConceptDetailsPanel } from '@/components/concepts/ConceptDetailsPanel'
import { ColorPickerDialog } from '@/components/toolbar/dialogs/ColorPickerDialog'
import { CanvasViewportProvider } from '@/contexts/CanvasViewportContext'
import { SaveStatusProvider, useSaveStatus } from '@/contexts/SaveStatusContext'
import type { ConceptCategory } from '@/db/Types'
import type { EditorTab } from '@/components/playbook-editor/Types'
import type { ConceptSide } from '@/types/Concept.types'
import type { Drawing } from '@/types'
import './concept-editor-page.css'

export function ConceptEditorPage() {
	return (
		<SaveStatusProvider>
			<ConceptEditorContent />
		</SaveStatusProvider>
	)
}

function ConceptEditorContent() {
	const { playbookId, conceptId: rawConceptId } = useParams<{ playbookId: string; conceptId: string }>()
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const initialCategory = (searchParams.get('category') as ConceptCategory) ?? 'route'

	// Treat "new" as no conceptId (create mode)
	const conceptId = rawConceptId === 'new' ? undefined : rawConceptId

	const [mode, setMode] = useState<'visual' | 'ai'>('visual')
	const [showPanel, setShowPanel] = useState(true)
	const { status: saveStatus, trackMutation } = useSaveStatus()

	const canvasDrawing = useCanvasDrawing()

	useKeyboardShortcuts({
		setDrawingState: canvasDrawing.updateState,
		currentTool: canvasDrawing.state.tool,
	})

	const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
	const conceptIdRef = useRef(conceptId)
	conceptIdRef.current = conceptId

	const { state, updateField, addAssignment, removeAssignment, save, isValid } = useConceptEditor({
		conceptId,
		initialCategory,
	})

	const aiParser = useConceptAIParser(playbookId!)

	// Debounced autosave — uses shared trackMutation for status tracking
	useEffect(() => {
		if (!state.isDirty || !isValid || !state.name.trim() || !playbookId) return

		clearTimeout(saveTimerRef.current)
		saveTimerRef.current = setTimeout(() => {
			trackMutation(async () => {
				const savedId = await save(playbookId, null)
				// For new concepts, silently update the URL without triggering a route change
				if (!conceptIdRef.current && savedId) {
					conceptIdRef.current = savedId
					window.history.replaceState(null, '', `/playbooks/${playbookId}/concept/${savedId}`)
				}
				return savedId
			}).catch(() => {})
		}, 1500)

		return () => clearTimeout(saveTimerRef.current)
	}, [state.isDirty, isValid, playbookId, save, navigate, trackMutation])

	// Map concept category back to the playbook editor tab
	const categoryToTab: Record<ConceptCategory, EditorTab> = {
		route: 'routes',
		blocking: 'blocking',
		motion: 'motion',
		protection: 'protection',
	}

	const handleBack = useCallback(() => {
		// Flush pending save before navigating
		if (saveTimerRef.current && state.isDirty && isValid && playbookId) {
			clearTimeout(saveTimerRef.current)
			trackMutation(() => save(playbookId, null)).catch(() => {})
		}
		const tab = categoryToTab[state.category] ?? 'routes'
		navigate(`/playbooks/${playbookId}?tab=${tab}`)
	}, [state.isDirty, isValid, playbookId, save, navigate, trackMutation, state.category])

	function handleAcceptAIParse() {
		if (!aiParser.parsedConcept) return
		const parsed = aiParser.parsedConcept

		updateField('name', parsed.name)
		updateField('category', parsed.category)
		updateField('targetingMode', parsed.targetingMode)
		updateField('defaultSide', parsed.defaultSide as ConceptSide | null)

		const assignments = parsed.assignments.map((a, i) => ({
			role: a.targetType === 'absolute_role' ? a.target : null,
			selector_type: a.targetType === 'relative_selector' ? a.target : null,
			selector_params: null,
			drawing_data: {} as Drawing,
			order_index: i,
		}))

		for (const assignment of assignments) {
			addAssignment(assignment)
		}

		setMode('visual')
	}

	return (
		<EditorPageLayout
			toolbar={
				<ConceptToolbar
					selectedTool={canvasDrawing.state.tool}
					onToolChange={canvasDrawing.setTool}
					color={canvasDrawing.state.color}
					onColorChange={canvasDrawing.setColor}
					hashAlignment={canvasDrawing.state.hashAlignment}
					onHashAlignmentChange={canvasDrawing.setHashAlignment}
					showColorPicker={canvasDrawing.state.showColorPicker}
					onShowColorPickerChange={canvasDrawing.setShowColorPicker}
					showDrawOptions={canvasDrawing.state.showDrawOptions}
					onShowDrawOptionsChange={canvasDrawing.setShowDrawOptions}
					lineStyle={canvasDrawing.state.lineStyle}
					lineEnd={canvasDrawing.state.lineEnd}
					brushSize={canvasDrawing.state.brushSize}
					pathMode={canvasDrawing.state.pathMode}
					onLineStyleChange={canvasDrawing.setLineStyle}
					onLineEndChange={canvasDrawing.setLineEnd}
					onBrushSizeChange={canvasDrawing.setBrushSize}
					onPathModeChange={canvasDrawing.setPathMode}
					saveStatus={saveStatus}
					showPanel={showPanel}
					onTogglePanel={() => setShowPanel(prev => !prev)}
				/>
			}
			header={
				<ConceptHeader
					name={state.name}
					onNameChange={(name) => updateField('name', name)}
					category={state.category}
					onCategoryChange={(category) => updateField('category', category)}
					mode={mode}
					onModeChange={setMode}
					onBackToPlaybook={handleBack}
				/>
			}
			canvasArea={
				<div className="concept-editor-canvas-area">
					<CanvasViewportProvider>
						<ConceptCanvas
							drawingState={{
								tool: canvasDrawing.state.tool,
								color: canvasDrawing.state.color,
								brushSize: canvasDrawing.state.brushSize,
								lineStyle: canvasDrawing.state.lineStyle,
								lineEnd: canvasDrawing.state.lineEnd,
								pathMode: canvasDrawing.state.pathMode,
								eraseSize: 40,
								snapThreshold: 20,
							}}
							hashAlignment={canvasDrawing.state.hashAlignment}
							targetingMode={state.targetingMode}
							initialAssignments={state.assignments.length > 0 ? state.assignments.map(a => ({
								role: a.role ?? null,
								selector_type: a.selector_type ?? null,
								selector_params: a.selector_params ?? null,
								drawing_data: a.drawing_data,
							})) : undefined}
							readonly={mode === 'ai'}
						/>
					</CanvasViewportProvider>

					{canvasDrawing.state.showColorPicker && (
						<div className="concept-editor-color-picker">
							<ColorPickerDialog
								currentColor={canvasDrawing.state.color}
								onColorChange={canvasDrawing.setColor}
								onClose={() => canvasDrawing.setShowColorPicker(false)}
								useRelativePosition={true}
							/>
						</div>
					)}
				</div>
			}
			sidePanel={
				<ConceptDetailsPanel
					showPanel={showPanel}
					mode={mode}
					targetingMode={state.targetingMode}
					onTargetingModeChange={(tm) => updateField('targetingMode', tm)}
					defaultSide={state.defaultSide}
					onDefaultSideChange={(side) => updateField('defaultSide', side)}
					assignments={state.assignments}
					onRemoveAssignment={removeAssignment}
					code={state.code}
					onCodeChange={(code) => updateField('code', code)}
					aiParser={aiParser}
					onAcceptAIParse={handleAcceptAIParse}
				/>
			}
		/>
	)
}
